import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import PortfolioSelector from './PortfolioSelector';
import { selectionFromParams, prefilled, prefilledRange } from '../lib/reportPrefill';
import { maxReportDate, defaultReportDate } from '../lib/reportDates';
import MaskedDateField from '../../../components/ui/MaskedDateField';

/**
 * Report Generation Popup
 *
 * Thin shell around the shared <PortfolioSelector> (groups vs accounts, @@
 * aggregate, fallback) + the date field(s). This is the reference pattern every
 * report popup follows. See docs/superpowers/specs/2026-06-24-report-groups-design.md.
 */
const ReportGenerationPopup = ({
  isOpen,
  onClose,
  onGenerate,
  reportType,
  reportName,
  prefillClientName = null,
  initialParams = null,
  darkMode = false,
}) => {
  const [selection, setSelection] = useState({ selectedItem: null, params: {} });
  const [date, setDate] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const needsDateRange = [
    'credit_migration_watchlist',
    'transaction_history',
  ].includes(reportType);

  // Reports default to yesterday (latest landed Pershing data).
  const defaultDate = defaultReportDate();

  useEffect(() => {
    if (isOpen) {
      setDate(prefilled(initialParams, 'date', defaultDate));
      const range = prefilledRange(initialParams, { start: '', end: '' });
      setStartDate(range.start);
      setEndDate(range.end);
      setErrors({});
      setSelection({ selectedItem: null, params: {} });
    }
  }, [isOpen, defaultDate, initialParams]);

  // The single thin-params builder used by handleGenerate.
  const buildParams = (sel) => {
    const params = { currency: 'USD', ...sel.params };
    if (needsDateRange) {
      params.date_range = { start: startDate, end: endDate };
      params.date = startDate;
    } else {
      params.date = date;
    }
    return params;
  };

  const validateForm = (sel = selection) => {
    const newErrors = {};
    if (!sel.selectedItem) newErrors.portfolio = 'Pick a group or account';
    if (needsDateRange) {
      if (!startDate) newErrors.startDate = 'Start date is required';
      if (!endDate) newErrors.endDate = 'End date is required';
      if (startDate && endDate && new Date(startDate) > new Date(endDate)) newErrors.dateRange = 'Start date must be before end date';
    } else if (!date) {
      newErrors.date = 'Date is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleGenerate = async (submitted) => {
    // `submitted` is the Enter-resolved selection from PortfolioSelector (state
    // hasn't flushed yet); a button click passes a MouseEvent, so fall back to
    // state when it carries no selectedItem.
    const sel = (submitted && submitted.selectedItem) ? submitted : selection;
    if (!validateForm(sel)) return;
    setIsLoading(true);
    try {
      await onGenerate(buildParams(sel));
      onClose();
    } catch (error) {
      console.error('Report generation error:', error);
      setErrors({ submit: error.message || 'Failed to generate report' });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const inputCls = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${darkMode ? 'bg-[#1a1a1a] border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'}`;
  const labelCls = `block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`rounded-lg p-6 max-w-md w-full mx-4 shadow-xl ${darkMode ? 'bg-[#141414]' : 'bg-white'}`}>
        <div className="flex items-center justify-between mb-6">
          <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Generate {reportName || 'Report'}
          </h3>
          <button onClick={onClose} className={`transition-colors ${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <PortfolioSelector darkMode={darkMode} prefill={prefillClientName || ''} initialSelection={selectionFromParams(initialParams)} onChange={setSelection} onSubmit={handleGenerate}  disabled={isLoading}/>
            {errors.portfolio && <p className="text-red-500 text-xs mt-1">{errors.portfolio}</p>}
          </div>

          {needsDateRange ? (
            <>
              <div>
                <label className={labelCls}>Start Date <span className="text-red-500">*</span></label>
                <MaskedDateField value={startDate} max={maxReportDate()} onChange={(e) => { setStartDate(e.target.value); setErrors((p) => ({ ...p, startDate: null, dateRange: null })); }} className={inputCls} disabled={isLoading} />
                {errors.startDate && <p className="text-red-500 text-xs mt-1">{errors.startDate}</p>}
              </div>
              <div>
                <label className={labelCls}>End Date <span className="text-red-500">*</span></label>
                <MaskedDateField value={endDate} max={maxReportDate()} onChange={(e) => { setEndDate(e.target.value); setErrors((p) => ({ ...p, endDate: null, dateRange: null })); }} className={inputCls} disabled={isLoading} />
                {errors.endDate && <p className="text-red-500 text-xs mt-1">{errors.endDate}</p>}
                {errors.dateRange && <p className="text-red-500 text-xs mt-1">{errors.dateRange}</p>}
              </div>
            </>
          ) : (
            <div>
              <label className={labelCls}>Report Date <span className="text-red-500">*</span></label>
              <MaskedDateField value={date} max={maxReportDate()} onChange={(e) => { setDate(e.target.value); setErrors((p) => ({ ...p, date: null })); }} className={inputCls} disabled={isLoading} />
              {errors.date && <p className="text-red-500 text-xs mt-1">{errors.date}</p>}
            </div>
          )}

          {errors.submit && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200"><p className="text-red-600 text-sm">{errors.submit}</p></div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button type="button" variant="outline" onClick={onClose} disabled={isLoading} className={darkMode ? 'border-gray-600 text-gray-300 hover:bg-[#1a1a1a]' : ''}>Cancel</Button>
          <Button type="button" onClick={handleGenerate} disabled={isLoading} className="bg-teal-600 hover:bg-teal-700 text-white">
            {isLoading ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>Generating...</>) : 'Generate'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReportGenerationPopup;
